/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

'use client';

import { cn } from '../../../lib/utils';
import Icons from '../../../icons/icons';
import { CreateEvalsJobsBody, EvalResults } from '../../../types/api';
import { formatEvalLabel, formatNumberByLocale } from '../../../lib/utils';
import { useLanguage } from '../../../providers/LanguageProvider';
import { getMergedEvalTimeline } from '../../../utils/eval-timeline';

import EvalListItem from './eval-list-item';
import CircularArcGraph from './circular-arc-graph';

type EvalTooltipProps = {
  evalObj: EvalResults | undefined;
  size: "default" | "compact",
  templateEnums?: string[];
  sendKahuPrompt?: () => void;
  sendExplanationRequest?: (body: CreateEvalsJobsBody) => void | Promise<void>;
  isExplaining?: boolean;
};

const EvalTooltip = ({ evalObj, size, templateEnums, sendKahuPrompt, sendExplanationRequest, isExplaining }: EvalTooltipProps) => {
    const { messages } = useLanguage();
    const evaluationLang = (messages as any)?.Evaluation;

    const totalEvalsCount = evalObj?.summary?.eval_count ?? 0;

    const enumCounts = evalObj?.summary?.enum_counts ?? {};
    const overallEvalResult = Object.entries(enumCounts).reduce(
        (topKey, [key, count]) =>
            count > (enumCounts[topKey] ?? -Infinity) ? key : topKey,
        Object.keys(enumCounts)[0]
    );

    const latestThreeEvals = getMergedEvalTimeline(evalObj);

  return (
    <>
       {/* Eval name / overall result */}
       <h3 className={cn("text-lg capitalize pb-1 mb-2 flex text-white items-center justify-between", { 'border-b border-[#0A343C]': totalEvalsCount <= 3 })}>
        {formatEvalLabel(evalObj?.eval_name)}
        {totalEvalsCount > 3 && <span className="text-sm text-[#8C9EA1]">{evaluationLang?.overall}: <span className="text-sm text-white">{formatEvalLabel(overallEvalResult)}</span></span>}
       </h3>

       {/* Summary Analytics when evals count is more than 3 */}
       {totalEvalsCount > 3 && (
        <header className="bg-[#08242F] pt-3 py-1.5 px-4 rounded-xl mb-3 grid grid-cols-4 gap-4">
           <div className="col-span-2 border-r border-[#0A343C] pr-4">
              <CircularArcGraph
                enumCounts={evalObj?.summary?.enum_counts}
                templateEnums={templateEnums}
              />
           </div>
           <div className="border-r border-[#0A343C] mt-1.5">
              <h4 className="text-[#8C9EA1] text-xs">{evaluationLang?.total_evaluations}</h4>
              <p className="text-2xl text-white mt-1 ml-0.5">{totalEvalsCount}</p>
           </div>
           <div>
              <h4 className="text-[#8C9EA1] text-xs mt-1.5">{evaluationLang?.total_tokens}</h4>
              <p className="text-2xl text-white mt-1 ml-0.5">{formatNumberByLocale(evalObj?.summary?.total_tokens)}</p>
           </div>
        </header>
     )}

       {/* Evals Data */}
       <div className="relative pb-3">
         <ul className="max-h-[300px] overflow-y-auto px-2">
           {latestThreeEvals.map((latestEval, index, arr) => (
               <EvalListItem
                   key={latestEval?.job_id || `${latestEval?.eval_timestamp}_${latestEval?.eval_result?.label}_${index}`}
                   templateName={evalObj?.eval_name}
                   latestEval={latestEval}
                   index={index}
                   arr={arr}
                   size={size}
                   sendExplanationRequest={sendExplanationRequest}
                   isExplaining={isExplaining}
               />
           ))}
         </ul>

        {/* Bottom fade indicating more scrollable content below */}
        {latestThreeEvals.length > 1 && (
            <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-[#0A1F26] to-transparent" />
        )}
       </div>

        {/* Kahu prompt info */}
        <footer 
            className="bg-[#061419] p-3 rounded-lg mt-2 flex items-center gap-3 cursor-pointer"
            onClick={sendKahuPrompt}
        >
            <span>
                {Icons.AskKahu('w-10 h-10')}
            </span>
            <div>
            <p className="text-xs text-[#8C9EA1]">
                {evaluationLang?.need_help_understanding_eval}
            </p>
            <p className="text-[13px] text-[#34BCAF] mt-0.5">
                {evaluationLang?.ask_kahu_default_prompt }
            </p>
            </div>
        </footer> 
    </>
  );
};

export default EvalTooltip;